import { errorWords, PAGE_SIZES } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type { TASK_CATEGORIES, TaskPriority, TaskStatus } from "@alm/domain";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { OffsetPaginationFooter } from "@/components/app/lists/offset-pagination-footer";

import { ReadFailed } from "@/components/app/lists/read-failed";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import {
  optionalDay,
  optionalInt,
  optionalOneOf,
  optionalTrue,
  stringParams,
  unlessAll,
  KEEP_PLACE,
  optionalSearch,
} from "@/lib/search";
import { useCanWithoutBuilding, usePolicy } from "@/lib/policy";
import { cn } from "@/lib/utils";
import { useZone } from "@/lib/facility/zone";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { TaskForm } from "./-components/task-form";
import { assigneesOf, EMPTY_TASK, linksOf, taskToInput } from "./-components/task-values";
import { TaskBoard } from "./-components/task-board";
import {
  NO_TASK_FILTERS,
  TaskFilters,
  taskFiltersActive,
  type TaskFilterValues,
} from "./-components/task-filters";
import { TaskTable } from "./-components/task-table";
import { useTaskChoices } from "./-components/use-task-choices";

type TaskSearch = {
  view?: "list";
  q?: string;
  status?: string;
  priority?: string;
  category?: string;
  assignee?: string;
  mine?: true;
  late?: true;
  dueFrom?: string;
  dueTo?: string;
  page?: number;
  size?: number;
};

export const Route = createFileRoute("/_authenticated/_support/task-center/")({
  validateSearch: (search: Record<string, unknown>): TaskSearch => ({
    view: optionalOneOf(["list"] as const, search.view),
    q: optionalSearch(search.q),
    ...stringParams(["status", "priority", "category", "assignee"], search),
    mine: optionalTrue(search.mine),
    late: optionalTrue(search.late),
    dueFrom: optionalDay(search.dueFrom),
    dueTo: optionalDay(search.dueTo),
    page: optionalInt(search.page, 2, 100000),
    size: optionalInt(search.size, 5, 200),
  }),
  component: TasksPage,
});

const OPEN_STATUSES: TaskStatus[] = ["open", "in_progress"];
const pick = <T extends string>(value: string): T[] | undefined =>
  value === "all" ? undefined : [value as T];

function TasksPage() {
  const { facilityId } = useFacility();
  const zone = useZone();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<TaskSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const {
    text: searchText,
    change: setSearchText,
    settling,
  } = useSearchParamText(params.q, (q) => patch({ q, page: undefined }));
  const filters: TaskFilterValues = {
    search: searchText,
    status: (params.status ?? NO_TASK_FILTERS.status) as TaskFilterValues["status"],
    priority: params.priority ?? "all",
    category: params.category ?? "all",
    assigneeId: params.assignee ?? "all",
    mine: Boolean(params.mine),
    pastDue: Boolean(params.late),
    dueFrom: params.dueFrom ?? "",
    dueTo: params.dueTo ?? "",
  };
  /* The board is what the old task centre opened on, and it is the view that
     answers the question people arrive with: how much is open. */
  const view: "board" | "list" = params.view ?? "board";
  const page = params.page ?? 1;
  const pageSize: number = params.size ?? PAGE_SIZES[1];
  const [creating, setCreating] = useState(false);
  // A new task names no building the API reads, so it is asked with none.
  const canManage = useCanWithoutBuilding("task:manage");
  const { subject } = usePolicy();
  const queryClient = useQueryClient();
  const search = params.q ?? "";
  const choices = useTaskChoices(creating);

  const status =
    view === "board" || filters.status === "all"
      ? undefined
      : filters.status === "active"
        ? OPEN_STATUSES
        : [filters.status];
  const list = useQuery({
    ...orpc.support.tasks.list.queryOptions({
      input: {
        facilityId,
        search: search || undefined,
        status,
        priority: pick<TaskPriority>(filters.priority),
        category: pick<(typeof TASK_CATEGORIES)[number]>(filters.category),
        assigneeId: filters.assigneeId === "all" ? undefined : filters.assigneeId,
        mine: filters.mine,
        pastDue: filters.pastDue,
        dueFrom: filters.dueFrom || undefined,
        dueTo: filters.dueTo || undefined,
        timezone: zone,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || list.isPlaceholderData;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.support.tasks.key() });
  const create = useMutation(
    orpc.support.tasks.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Task created");
        setCreating(false);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const move = useMutation(
    orpc.support.tasks.update.mutationOptions({
      onSuccess: invalidate,
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const changeFilters = (change: Partial<TaskFilterValues>) => {
    const next = { ...filters, ...change };
    if (next.search !== filters.search) setSearchText(next.search);
    patch({
      status: next.status === NO_TASK_FILTERS.status ? undefined : next.status,
      priority: unlessAll(next.priority),
      category: unlessAll(next.category),
      assignee: unlessAll(next.assigneeId),
      mine: next.mine ? true : undefined,
      late: next.pastDue ? true : undefined,
      dueFrom: next.dueFrom || undefined,
      dueTo: next.dueTo || undefined,
      page: undefined,
    });
  };
  /* The server states when today began in the building's clock; the epoch
     stands in only while nothing has loaded, when no row is compared to it. */
  const startOfToday = new Date(list.data?.startOfToday ?? 0);
  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;

  // The list's footer sits inside its table card; the board's under the board.
  const footer =
    total > PAGE_SIZES[0] ? (
      <OffsetPaginationFooter
        offset={(page - 1) * pageSize}
        pageSize={pageSize}
        total={total}
        noun="tasks"
        onOffsetChange={(offset) => {
          const next = offset / pageSize + 1;
          patch({ page: next > 1 ? next : undefined });
        }}
        onPageSizeChange={(next) =>
          patch({ size: next === PAGE_SIZES[1] ? undefined : next, page: undefined })
        }
      />
    ) : null;

  return (
    <>
      <PageHeader
        title="Task center"
        count={list.data ? total : undefined}
        meta={
          /* How many of them are late, which is the half a person acts on. */
          list.data
            ? `${
                rows.filter(
                  (row) => row.dueDate && new Date(row.dueDate) < startOfToday && !row.completedAt,
                ).length
              } overdue on this page, across your facilities.`
            : undefined
        }
        actions={
          <>
            <ToggleGroup
              type="single"
              variant="outline"
              aria-label="View"
              value={view}
              onValueChange={(next) => {
                if (next !== "board" && next !== "list") return;
                patch({ view: next === "list" ? "list" : undefined, page: undefined });
              }}
            >
              <ToggleGroupItem value="board">Board</ToggleGroupItem>
              <ToggleGroupItem value="list">List</ToggleGroupItem>
            </ToggleGroup>
            {canManage ? <Button onClick={() => setCreating(true)}>Create task</Button> : null}
          </>
        }
      />
      <TaskFilters
        filters={filters}
        onChange={changeFilters}
        people={choices.people}
        showStatus={view === "list"}
      />
      {list.isPending || (searching && rows.length === 0) ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <ReadFailed
          message="Could not load the tasks."
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : !rows.length ? (
        /* One empty state for both views: four columns each saying "No tasks"
           gave no way forward. */
        <EmptyState
          title={taskFiltersActive(filters) ? "No tasks match these filters" : "No tasks yet"}
          description={
            taskFiltersActive(filters)
              ? "Clear the filters to see every task."
              : "Tasks you create or are assigned appear here, on the board and in the list."
          }
          action={
            taskFiltersActive(filters) ? (
              <Button variant="outline" onClick={() => changeFilters(NO_TASK_FILTERS)}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div
          aria-busy={searching || undefined}
          className={cn("transition-opacity", searching && "opacity-60")}
        >
          {view === "board" ? (
            <TaskBoard
              rows={rows}
              startOfToday={startOfToday}
              onMove={
                canManage ? (taskId, next) => move.mutate({ taskId, status: next }) : undefined
              }
            />
          ) : (
            <TaskTable rows={rows} startOfToday={startOfToday} footer={footer} />
          )}
        </div>
      )}
      {view === "board" && footer ? <Card className="py-0">{footer}</Card> : null}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>New task</DialogTitle>
            <DialogDescription>
              A title is enough. A due day and a person make sure it reaches somebody.
            </DialogDescription>
          </DialogHeader>
          {/* The pinned building is the one the task is for, until somebody says otherwise. */}
          <TaskForm
            mode="create"
            defaultValues={{
              ...EMPTY_TASK,
              primaryFacilityId: facilityId === "all" ? EMPTY_TASK.primaryFacilityId : facilityId,
            }}
            facilities={choices.facilities}
            residents={choices.residents}
            leads={choices.leads}
            people={choices.people}
            peopleLoading={choices.peopleLoading}
            currentUserId={subject.userId}
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(values) =>
              create.mutate({
                ...taskToInput(values),
                assignees: assigneesOf(values),
                links: linksOf(values),
              })
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
